import React, { useEffect, useState } from 'react';
import Modal from './Modal';

interface VideoFile {
  name: string;
  url: string;
  size?: number;
}

interface MontageData {
  status: 'available' | 'processing' | 'not-found';
  montagePath?: string;
  message?: string;
}

const VideoGallery: React.FC = () => {
  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [montage, setMontage] = useState<MontageData | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const searchParams = new URLSearchParams(window.location.search);
  const folder = searchParams.get('folder') || '';  // Funzione per gestire il download su iOS e altri dispositivi
  const handleDownload = async (url: string, filename: string) => {
    // Detects if device is on iOS 
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      setIsDownloading(true);

      try {
        // Primo tentativo: prova con fetch e blob
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'video/mp4,video/*,*/*'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        // Verifica se il blob è valido
        if (blob.size === 0) {
          throw new Error('Blob vuoto ricevuto');
        }

        // Crea un URL del blob con tipo MIME specifico
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'video/mp4' }));

        // Crea un link con attributi ottimizzati per iOS
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Aggiunge il link al DOM e simula il click
        document.body.appendChild(link);
        link.click();

        // Pulisce immediatamente
        document.body.removeChild(link);

        // Ritarda la pulizia dell'URL per assicurarsi che il download inizi
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);

        // Mostra un messaggio di istruzioni per iOS
        setTimeout(() => {
          alert('Su iOS: se il video si apre in una nuova scheda, tieni premuto sul video e seleziona "Salva su file" per scaricarlo nella galleria.');
        }, 500);

      } catch (error) {
        console.error('Errore nel download blob:', error);

        // Fallback: apri il video in una nuova finestra con istruzioni
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

        if (newWindow) {
          // Mostra istruzioni dopo l'apertura
          setTimeout(() => {
            alert('Il video si è aperto in una nuova scheda. Tieni premuto sul video e seleziona "Salva su file" per scaricarlo.');
          }, 1000);
        } else {
          // Se il popup è bloccato, prova con location
          window.location.href = url;
        }
      } finally {
        setIsDownloading(false);
      }
    } else {
      // Per dispositivi non iOS, usa il download normale
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Fallback anche per altri dispositivi
        window.open(url, '_blank');
      }
    }
  };

  useEffect(() => {
    if (!folder) {
      setError('Nessun codice inserito.');
      return;
    }

    const endpoint = `${API_URL}/retrive-folder/${folder}?detailed=true`;

    fetch(endpoint, { method: 'GET', cache: 'no-store' })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.exists) {
          setShowModal(true);
          return;
        }
        const videosArray = data.files.map((file: any) => {
          if (typeof file === 'string') {
            return {
              name: file,
              url: `${API_URL}/uploads/${folder}/${file}`
            };
          } else {
            return {
              name: file.name,
              url: `${API_URL}/uploads/${folder}/${file.name}`,
              size: file.size,
            };
          }
        });
        setVideos(videosArray);
      })
      .catch(() => {
        setError('Errore nel recupero dei file.');
      });
  }, [folder, API_URL]);

  useEffect(() => {
    if (folder) {
      fetch(`${API_URL}/retrive-montage/${folder}`)
        .then(async (response) => {
          const data = await response.json();
          if (response.status === 200) {
            setMontage({ status: 'available', montagePath: data.montagePath });
          } else if (response.status === 202) {
            setMontage({ status: 'processing', montagePath: data.montagePath, message: data.message });
          } else if (response.status === 404) {
            setMontage({ status: 'not-found', message: data.error });
          } else {
            setMontage({ status: 'not-found', message: 'Errore nel recupero del montage' });
          }
        })
        .catch(() => {
          setMontage({ status: 'not-found', message: 'Errore nel recupero del montage' });
        });
    }
  }, [folder, API_URL]);

  const videosA = videos.filter(video => video.name.startsWith("RLC-810A"));
  const videosB = videos.filter(video => video.name.startsWith("RLC-810B"));

  const handleModalClose = () => {
    setShowModal(false);
    window.location.href = '/';
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col items-center justify-center pt-20 p-4 md:p-6 relative">
      {/* Header alert banner */}
      <div className="absolute top-0 w-full px-4 bg-gradient-to-r from-yellow-300 to-yellow-200 text-center py-2 md:py-3 text-gray-700 font-semibold shadow-md animate-pulse">
        <span className="text-sm md:text-base">
          I file vengono trattenuti solamente per 3 giorni. Scaricali prima che scadano!
        </span>
      </div>      {/* Banner informativo per iOS */}
      {/iPad|iPhone|iPod/.test(navigator.userAgent) && (
        <div className="absolute top-16 md:top-20 w-full px-4 bg-gradient-to-r from-blue-100 to-blue-50 text-center py-3 md:py-4 text-blue-800 text-sm shadow-md">
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-semibold">Istruzioni per iOS/iPhone/iPad</span>
            </div>
            <span className="text-xs leading-relaxed">
              Clicca "Scarica" → Il video si aprirà → Clicca sul tasto per condividere → Seleziona "Salva su file" per salvarlo nella galleria
            </span>
          </div>
        </div>)}<h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-8 ${/iPad|iPhone|iPod/.test(navigator.userAgent) ? 'mt-32 md:mt-36' : 'mt-16'}`}>
        Video Gallery - {folder}
      </h1>
      {error && <p className="text-red-500 text-center mb-4">{error}</p>}

      <div className="flex flex-col md:flex-row gap-8 w-full max-w-5xl">
        {/* Colonna per RLC-810B */}
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-center">Lato Sinistro</h2>
          <div className="space-y-6">            {videosB.map(video => (
            <div key={video.name} className="bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-4 md:p-6 rounded-2xl shadow-lg border border-gray-100">
              <video className="w-full" controls>
                <source src={video.url} type="video/mp4" />
                Il tuo browser non supporta il tag video.
              </video>                <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-sm sm:text-base font-medium">
                  Nome file: {video.name}
                  {video.size && (
                    <div className="text-xs sm:text-sm text-gray-600">
                      Peso File: {(video.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  )}
                </div>                  <button
                  onClick={() => handleDownload(video.url, video.name)}
                  disabled={isDownloading}
                  className={`px-4 py-2 font-semibold rounded-lg shadow-md transition-all duration-300 flex items-center gap-2 text-sm ${isDownloading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                    }`}
                >
                  {isDownloading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Preparando...
                    </>) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Scarica
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
        {/* Colonna per RLC-810A */}
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-center">Lato Destro</h2>
          <div className="space-y-6">            {videosA.map(video => (
            <div key={video.name} className="bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-4 md:p-6 rounded-2xl shadow-lg border border-gray-100">
              <video className="w-full" controls>
                <source src={video.url} type="video/mp4" />
                Il tuo browser non supporta il tag video.
              </video>                <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-sm sm:text-base font-medium">
                  Nome file: {video.name}
                  {video.size && (
                    <div className="text-xs sm:text-sm text-gray-600">
                      Peso File: {(video.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  )}
                </div>                  <button
                  onClick={() => handleDownload(video.url, video.name)}
                  disabled={isDownloading}
                  className={`px-4 py-2 font-semibold rounded-lg shadow-md transition-all duration-300 flex items-center gap-2 text-sm ${isDownloading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                    }`}
                >
                  {isDownloading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Preparando...
                    </>) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Scarica
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>

      {/* Sezione dedicata al Montage degli Highlights */}
      <div className="mt-12 w-full max-w-4xl">
        <h2 className="text-2xl font-bold text-center mb-4">Highlights Montage</h2>
        {montage === null ? (
          <p className="text-center">Caricamento montage...</p>
        ) : montage.status === 'processing' ? (
          <p className="text-center text-yellow-600">
            {montage.message || 'Il montage è in elaborazione. Per favore, attendere...'}
          </p>) : montage.status === 'available' && montage.montagePath ? (
            <div className="bg-white bg-opacity-90 backdrop-filter backdrop-blur-sm p-4 md:p-6 rounded-2xl shadow-lg border border-gray-100">
              <video className="w-full" controls>
                <source
                  src={`https://api.asddonvittorio.it/uploads/${folder}/Highlights.mp4`}
                  type="video/mp4"
                />
                Il tuo browser non supporta il tag video.
              </video>            <div className="mt-4 flex justify-center">
                <button
                  onClick={() => handleDownload(`https://api.asddonvittorio.it/uploads/${folder}/Highlights.mp4`, "Highlights.mp4")}
                  disabled={isDownloading}
                  className={`px-6 py-3 font-semibold rounded-lg shadow-md transition-all duration-300 flex items-center gap-2 ${isDownloading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white'
                    }`}
                >
                  {isDownloading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Preparando...
                    </>) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Scarica Highlights
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
          <p className="text-center text-red-500">
            {montage.message || 'Montage non disponibile.'}
          </p>
        )}
      </div>

      {showModal && (
        <Modal onClose={() => handleModalClose()}>
          <div className="text-center">
            <h2 className="text-xl font-bold mb-2">Errore</h2>
            <p>Il codice potrebbe non essere valido o i file potrebbero essere scaduti.</p>
          </div>
        </Modal>
      )}

      <footer className="mt-8 md:mt-12 w-full max-w-md mx-auto text-xs md:text-sm text-gray-600 bg-white bg-opacity-50 backdrop-filter backdrop-blur-sm py-2 md:py-3 px-4 md:px-6 rounded-full shadow-md transform transition-all hover:scale-105">
        <a
          href="https://www.instagram.com/vittorio.carcatella"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-wrap justify-center items-center gap-1 md:gap-2 hover:text-blue-600 transition-colors"
        >
          <span className="italic">
            Fatto con il <span className="inline-block mx-1 text-red-500 animate-pulse transform hover:scale-125 transition-all">❤️</span> da
          </span>
          <span className="font-bold text-blue-500 border-b border-blue-200 pb-px">Vittorio Carcatella</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 md:h-4 w-3 md:w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </footer>
    </div>
  );
};

export default VideoGallery;